import { useCallback, useEffect, useRef, useState } from 'react';

import debounce from 'lodash-es/debounce';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { dialogsEndpoints } from '@/api/endpoints';
import { useBrainVariables } from '@/hooks/useBrainVariables';
import useDialogAlert from '@/hooks/useDialogAlert';
import { useValidateDraft } from '@/hooks/useValidateDraft';
import {
  selectSelectedCondition,
  selectSelectedConditionSiblings,
  selectSelectedNode,
} from '@/redux/dialogs/selectors';
import { selectBrainId, selectDialogId } from '@/redux/session/selectors';
import type { ValidateConditionBody } from '@/shared/types/authoring-validation';
import type { RootState } from '@/shared/types/state';
import { countWords } from '@/util/strings';
import { GUIDELINE_MIN_WORDS } from '@/util/validation/schemas/dialog-schemas';

const DEBOUNCE_MS = 1000;
const GUIDELINE_VALIDATION_ALERT_FIELD = 'guidelineValidation';

const isReadyToValidate = (value: string) => countWords(value.trim()) >= GUIDELINE_MIN_WORDS;

interface UseConditionValidatorArgs {
  matchIsGuideline: boolean;
  guidelineValue: string;
  /**
   * `true` once the user has focused the guideline editor. Blocks validation against
   * pre-loaded text the author hasn't engaged with (e.g. opening a condition and editing
   * only the name field). Flip back to `false` on condition switch to re-arm.
   * Defaults to `true` so direct/test callers keep the pre-gating behavior.
   */
  hasInteracted?: boolean;
}

export const useConditionValidator = ({
  matchIsGuideline,
  guidelineValue,
  hasInteracted = true,
}: UseConditionValidatorArgs) => {
  const { t } = useTranslation();
  const brainId = useSelector(selectBrainId);
  const dialogId = useSelector(selectDialogId);
  const condition = useSelector(selectSelectedCondition);
  const siblings = useSelector(selectSelectedConditionSiblings);
  const selectedNode = useSelector(selectSelectedNode);
  const selectedConditionIndex = useSelector(
    (state: RootState) => state.nodes.selectedConditionIndex
  );
  const nodeId = selectedNode?.node_id ?? '';
  const conditionIndex = selectedConditionIndex ?? 0;
  const conditionId = condition?.condition_id ?? '';
  const { contextVariables } = useBrainVariables();
  const [status, setStatus] = useState<'idle' | 'validating' | 'invalid' | 'improve'>('idle');
  const [recommendation, setRecommendation] = useState('');

  const endpoint = brainId && dialogId ? dialogsEndpoints.validate(brainId, dialogId) : '';
  const { validate } = useValidateDraft<ValidateConditionBody>({ endpoint });
  const { setFieldWarning } = useDialogAlert({
    id: conditionId,
    nodeId,
    title: t('actions.types.condition'),
    type: 'condition',
    index: conditionIndex,
  });

  const currentConditionIdRef = useRef<string>(conditionId);
  const currentGuidelineValueRef = useRef<string>(guidelineValue);
  const lastValidatedValueRef = useRef('');
  const lastSentRecommendationRef = useRef('');
  // Mirrors of `status`/`recommendation` so the main effect can re-sync the Redux alert
  // without listing them in its deps (which would cause the effect to run after every
  // validation result and fight with the value/match-type triggered runs).
  const statusRef = useRef<'idle' | 'validating' | 'invalid' | 'improve'>('idle');
  const recommendationRef = useRef('');

  const resetValidation = useCallback(() => {
    setStatus('idle');
    setRecommendation('');
    setFieldWarning(GUIDELINE_VALIDATION_ALERT_FIELD, undefined);
  }, [setFieldWarning]);

  useEffect(() => {
    currentConditionIdRef.current = conditionId;
  }, [conditionId]);

  useEffect(() => {
    currentGuidelineValueRef.current = guidelineValue;
  }, [guidelineValue]);

  useEffect(() => {
    statusRef.current = status;
    recommendationRef.current = recommendation;
  }, [status, recommendation]);

  // Clear the *previous* condition's verdict when conditionId changes (or on unmount).
  // Running this in cleanup means the closure still holds the prior setFieldError, which is
  // bound to the prior conditionId — without that, the dispatch would target the new
  // condition and leave the old condition's alert stuck in Redux.
  useEffect(() => {
    return () => {
      lastValidatedValueRef.current = '';
      lastSentRecommendationRef.current = '';
      resetValidation();
    };
  }, [conditionId, resetValidation]);

  const runValidate = useCallback(
    async (value: string) => {
      if (!conditionId || !condition || !endpoint) {
        return;
      }

      setStatus('validating');
      setRecommendation('');

      const outcome = await validate(
        { condition, siblings, context_variables: contextVariables },
        lastSentRecommendationRef.current || undefined
      );

      // Drop the result if the user has switched conditions or moved on to different text
      // while the request was in flight — applying it would set a stale verdict against text
      // the author has already replaced.
      if (currentConditionIdRef.current !== conditionId) {
        return;
      }
      if (currentGuidelineValueRef.current.trim() !== value) {
        return;
      }

      if (outcome.kind === 'degraded') {
        resetValidation();
        return;
      }

      // Remember we validated this exact text, even when the backend returned no
      // recommendation, so a later re-render with unchanged text doesn't re-ping.
      lastValidatedValueRef.current = value;

      const { valid, recommendation: nextRecommendation } = outcome.result;
      if (!nextRecommendation) {
        lastSentRecommendationRef.current = '';
        resetValidation();
        return;
      }
      lastSentRecommendationRef.current = nextRecommendation;
      const nextStatus: 'invalid' | 'improve' = valid ? 'improve' : 'invalid';
      setRecommendation(nextRecommendation);
      setStatus(nextStatus);
      setFieldWarning(
        GUIDELINE_VALIDATION_ALERT_FIELD,
        nextStatus === 'invalid' ? nextRecommendation : undefined
      );
    },
    [
      condition,
      conditionId,
      contextVariables,
      endpoint,
      resetValidation,
      setFieldWarning,
      siblings,
      validate,
    ]
  );

  // Keep `runValidate` reachable from a stable debounced function. Recreating the debounced
  // function whenever `runValidate`'s identity changed (e.g., when Redux gave us a new
  // `condition` reference 200ms after a keystroke) cancelled the pending call without
  // rescheduling it, so validation never fired.
  const runValidateRef = useRef(runValidate);
  useEffect(() => {
    runValidateRef.current = runValidate;
  }, [runValidate]);

  const debouncedValidateRef = useRef(
    debounce((value: string) => runValidateRef.current(value), DEBOUNCE_MS)
  );

  useEffect(() => {
    if (!conditionId) {
      return;
    }

    if (!matchIsGuideline || !hasInteracted) {
      // Preserve any existing verdict so toggling match / refocusing with unchanged text
      // doesn't trigger a redundant validate call. The UI hides the banner while match
      // !== 'guideline'; `hasInteracted` stops us validating pre-loaded text the user
      // hasn't engaged with yet.
      debouncedValidateRef.current.cancel();
      return;
    }

    if (!isReadyToValidate(guidelineValue)) {
      debouncedValidateRef.current.cancel();
      lastValidatedValueRef.current = '';
      resetValidation();
      return;
    }

    const trimmed = guidelineValue.trim();
    if (lastValidatedValueRef.current === trimmed) {
      // Text hasn't changed since the last validation. If an outside actor (e.g. the
      // parent's match-type toggle dispatching `clearDialogAlerts`) wiped the Redux alert
      // while we preserved the local verdict, re-register it now so the alerts panel stays
      // consistent with the banner the user sees.
      if (statusRef.current === 'invalid' && recommendationRef.current) {
        setFieldWarning(GUIDELINE_VALIDATION_ALERT_FIELD, recommendationRef.current);
      }
      return;
    }

    if (lastValidatedValueRef.current !== '') {
      // Previously validated text just changed — clear the stale verdict now so the banner
      // doesn't linger on a recommendation the user has already moved past.
      lastValidatedValueRef.current = '';
      resetValidation();
    }

    debouncedValidateRef.current(trimmed);
  }, [
    conditionId,
    guidelineValue,
    hasInteracted,
    matchIsGuideline,
    resetValidation,
    setFieldWarning,
  ]);

  useEffect(() => {
    const debounced = debouncedValidateRef.current;
    return () => {
      debounced.cancel();
    };
  }, []);

  return { status, recommendation };
};
